import { assign, createMachine } from "xstate";
import { changePassword } from "@nju/data-access-portal-user";
import {
  hasAtLeastOneDigit,
  hasLowerAndUpperCase,
  hasSpecialChar,
} from "@nju/utils";

export interface IContext {
  oldPassword: string;
  newPassword: string;
  repeatNewPassword: string;
  oldPasswordError?: string;
  newPasswordError?: string;
  repeatNewPasswordError?: string;
}

type IEvent =
  | {
      type: "SET";
      name: "oldPassword" | "newPassword" | "repeatNewPassword";
      value: string;
    }
  | { type: "SEND" }
  | { type: "ERROR" }
  | { type: "SENT" }
  | { type: "NEW_PASSWORD_SAME_AS_CURRENT" }
  | { type: "LAST_FIVE_PASSWORDS" }
  | { type: "INVALID_CURRENT_PASSWORD" };

export const changePasswordMachine = createMachine(
  {
    context: {
      oldPassword: "",
      newPassword: "",
      repeatNewPassword: "",
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "init",
    states: {
      init: {
        on: {
          SET: {
            actions: "setEvent",
          },
          SEND: {
            target: "validateForm",
          },
        },
      },
      validateForm: {
        entry: "validateForm",
        always: [
          {
            cond: "isValid",
            target: "submit",
          },
          {
            target: "init",
          },
        ],
      },
      submit: {
        invoke: {
          src: "sendPassword",
          onDone: "success",
          onError: "error",
        },
        on: {
          SENT: {
            target: "success",
          },
          ERROR: {
            target: "error",
          },
          NEW_PASSWORD_SAME_AS_CURRENT: {
            actions: ["setFormError"],
            target: "init",
          },
          LAST_FIVE_PASSWORDS: {
            actions: ["setFormError"],
            target: "init",
          },
          INVALID_CURRENT_PASSWORD: {
            actions: ["setFormError"],
            target: "init",
          },
        },
      },
      success: {
        type: "final",
      },
      error: {
        type: "final",
      },
    },
  },
  {
    guards: {
      isValid: ({
        oldPasswordError,
        newPasswordError,
        repeatNewPasswordError,
      }) => {
        return (
          oldPasswordError === undefined &&
          newPasswordError === undefined &&
          repeatNewPasswordError === undefined
        );
      },
    },
    actions: {
      setEvent: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET") {
          return {
            [event.name]: event.value,
          };
        }
        return context;
      }),
      validateForm: assign<IContext, IEvent>({
        oldPasswordError: ({ oldPassword }) => {
          if (!oldPassword) {
            return "Pole jest wymagane.";
          }
          return;
        },
        newPasswordError: ({ newPassword }) => {
          if (!newPassword) {
            return "Pole jest wymagane.";
          } else if (
            !hasAtLeastOneDigit(newPassword) ||
            !hasSpecialChar(newPassword) ||
            !hasLowerAndUpperCase(newPassword) ||
            newPassword.length < 8
          ) {
            return "Wpisz hasło, które spełnia kryteria.";
          }
          return;
        },
        repeatNewPasswordError: ({ newPassword, repeatNewPassword }) => {
          if (!repeatNewPassword) {
            return "Pole jest wymagane.";
          }
          if (repeatNewPassword !== newPassword) {
            return "Hasła muszą być takie same.";
          }
          return;
        },
      }),
      setFormError: assign<IContext, IEvent>((context, event) => {
        if (event.type === "INVALID_CURRENT_PASSWORD") {
          return {
            oldPasswordError: "Hasło jest niepoprawne.",
          };
        }
        if (event.type === "NEW_PASSWORD_SAME_AS_CURRENT") {
          return {
            newPasswordError:
              "Nowe hasło nie może być takie samo jak aktualne hasło.",
          };
        }
        if (event.type === "LAST_FIVE_PASSWORDS") {
          return {
            newPasswordError:
              "Nowe hasło nie może być takie samo jak ostatnich 5 haseł.",
          };
        }
        return context;
      }),
    },
    services: {
      sendPassword: (context) => {
        return async (callback) => {
          const result = await changePassword(
            context.oldPassword,
            context.newPassword,
          );

          if (result.isOk) {
            return callback({ type: "SENT" });
          }

          if (result.err.code === "INVALID_CURRENT_PASSWORD") {
            return callback({ type: "INVALID_CURRENT_PASSWORD" });
          }

          if (result.err.code === "NEW_PASSWORD_SAME_AS_CURRENT") {
            return callback({ type: "NEW_PASSWORD_SAME_AS_CURRENT" });
          }

          if (result.err.code === "LAST_FIVE_PASSWORDS") {
            return callback({ type: "LAST_FIVE_PASSWORDS" });
          }

          return callback({ type: "ERROR" });
        };
      },
    },
  },
);
